import { Locale } from 'date-fns';
import { enUS, frCA, es } from 'date-fns/locale'; // import all locales we need
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import { z } from 'zod';
import { makeZodI18nMap } from 'zod-i18n-map';

const SUPPORTED_LOCALE_VALUES = {
  en: 'en',
  ca: 'ca',
  es: 'es',
} as const;
const supportedLocalesSchema = z.nativeEnum(SUPPORTED_LOCALE_VALUES);
export type SupportedLocales = z.infer<typeof supportedLocalesSchema>;

export const supportedDateFnsLocales: Record<SupportedLocales, Locale> = {
  en: enUS,
  ca: frCA,
  es: es,
};

export const toSupportedLocaleValue = (maybeLocale: unknown) =>
  supportedLocalesSchema.catch('en').parse(maybeLocale);

let clientId: number | null = null;

export const setClientIdForLocales = (id: number | null) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('[i18n] Setting clientId for locales from', clientId, 'to', id);
  }

  const previousClientId = clientId;

  // No change, skip the reload
  if (previousClientId === id) {
    return;
  }

  clientId = id;

  // Handle clearing clientId (logout scenario)
  if (id === null && previousClientId !== null) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[i18n] Clearing clientId, reverting to default locales');
    }
    const currentLang = i18n.language;
    i18n.removeResourceBundle(currentLang, 'common');
    i18n.reloadResources(currentLang, 'common').then(() => {
      i18n.changeLanguage(currentLang);
    });
    return;
  }

  // Handle setting or changing clientId (including initial set)
  if (clientId !== null) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[i18n] ClientId changed from', previousClientId, 'to', clientId, '- reloading translations');
    }

    // Remove the cached common namespace translations
    const currentLang = i18n.language;
    i18n.removeResourceBundle(currentLang, 'common');

    // Reload the common namespace to trigger reload with new path
    i18n.reloadResources(currentLang, 'common').then(() => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[i18n] Reloaded resources for clientId:', clientId);
      }
      // Force a language change to ensure all components re-render
      i18n.changeLanguage(currentLang);
    });
  }
};

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    backend: {
      addPath: '/locales/{{lng}}/{{ns}}/',
      crossDomain: true,
      loadPath: (lngs: string[], namespaces: string[]) => {
        const lng = lngs[0];
        const ns = namespaces[0];

        if (process.env.NODE_ENV === 'development') {
          console.log('[i18n] loadPath called with lng:', lng, 'ns:', ns, 'clientId:', clientId);
        }

        // Normalize language code (en-US -> en, fr-CA -> ca, etc.)
        let normalizedLng = lng;
        if (lng === 'en-US' || lng === 'en-CA') {
          normalizedLng = lng === 'en-CA' ? 'ca' : 'en';
        } else if (lng === 'fr-CA') {
          normalizedLng = 'ca';
        } else if (lng.startsWith('es')) {
          normalizedLng = 'es';
        }

        // If clientId is set, try client-specific locale first
        if (clientId && ns === 'common') {
          const path = `/locales/${clientId}/${normalizedLng}/${ns}.json`;
          if (process.env.NODE_ENV === 'development') {
            console.log('[i18n] Returning client-specific path:', path);
          }
          return path;
        }

        // Default path
        const path = `/locales/${normalizedLng}/${ns}.json`;
        if (process.env.NODE_ENV === 'development') {
          console.log('[i18n] Returning default path:', path);
        }
        return path;
      },
      request: (_options: any, url: string, _payload: any, callback: (error: any, response: { status: number; data: string }) => void) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('[i18n] Fetching URL:', url);
        }

        // A missing locale file can still come back as an HTTP 200 (e.g. an
        // SPA history-API fallback serving index.html), so a real JSON body
        // is required to consider the fetch successful, not just `response.ok`.
        const fetchLocaleJson = (fetchUrl: string) =>
          fetch(fetchUrl).then((response) => {
            if (!response.ok) {
              throw new Error(
                `Failed loading ${fetchUrl}: ${response.status}`,
              );
            }
            return response.text().then((text) => {
              try {
                JSON.parse(text);
              } catch {
                throw new Error(`Not a valid locale file: ${fetchUrl}`);
              }
              return text;
            });
          });

        fetchLocaleJson(url)
          .catch((error) => {
            // If client-specific locale doesn't exist, fall back to default
            if (clientId && url.includes(`/locales/${clientId}/`)) {
              const fallbackUrl = url.replace(`/locales/${clientId}/`, '/locales/');
              if (process.env.NODE_ENV === 'development') {
                console.log('[i18n] Client-specific locale not found, falling back to:', fallbackUrl);
              }
              return fetchLocaleJson(fallbackUrl);
            }
            throw error;
          })
          .then((data) => {
            callback(null, { status: 200, data });
          })
          .catch((error) => {
            callback(error, { status: 500, data: '' });
          });
      },
    },
    debug: process.env.NODE_ENV === 'development',
    fallbackLng: {
      en: ['en'],
      'en-US': ['en'],
      'en-CA': ['ca'],
      'fr-CA': ['ca'],
      es: ['es'],
      'es-ES': ['es'],
      default: ['en'],
    },
    interpolation: {
      escapeValue: false, // react already safes from xss
    },
    saveMissing: true,
    saveMissingTo: 'all',
    defaultNS: ['common', 'glossary'],
    ns: ['common', 'glossary'],
    parseMissingKeyHandler() {
      return '__NOT_TRANSLATED__';
    },
    missingKeyHandler() {
      return '__NOT_TRANSLATED__';
    },
    partialBundledLanguages: true,
  });

z.setErrorMap(
  makeZodI18nMap({
    handlePath: false,
  }),
);

export default i18n;
export { i18n, SUPPORTED_LOCALE_VALUES };
